import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  configApiRef,
  IdentityApi,
  SignInPageProps,
  useApi,
} from '@backstage/core-plugin-api';
import { UserIdentity } from '@backstage/core-components';
import { LandingPage } from '../LangingPage/LandingPage';
import { Root, RootWithLandingPageView } from '../Root';
import { FullScreenContainer } from './CustomSignInPage.styles';
import { logtoOIDCAuthApiRef } from '../../apis';
import ContactUsPage from '../ContactUs/ContactUsPage';
import { useOpenLogin } from './useOpenLogin';

export function LandingSignInPage({ onSignInSuccess }: SignInPageProps) {
  const configApi = useApi(configApiRef);
  const isNewLandingPage = Boolean(
    configApi.getOptionalBoolean('featureFlags.newLandingPage'),
  );
  const location = useLocation();
  const isContactUsRoute = location.pathname.startsWith('/contact-us');
  const [isRestoringSession, setIsRestoringSession] = useState(true);

  const logtoAuthApi = useApi(logtoOIDCAuthApiRef);

  const handleSignInSuccess = useCallback(
    (identityApi: IdentityApi) => {
      onSignInSuccess(identityApi);
    },
    [onSignInSuccess],
  );

  const openLogin = useOpenLogin(handleSignInSuccess);

  useEffect(() => {
    let isMounted = true;
    const restoreSession = async () => {
      try {
        const identityResponse = await logtoAuthApi.getBackstageIdentity({
          optional: true,
        });
        if (!identityResponse || !isMounted) {
          return;
        }
        const profile = await logtoAuthApi.getProfile();
        if (!isMounted) {
          return;
        }
        handleSignInSuccess(
          UserIdentity.create({
            identity: identityResponse.identity,
            authApi: logtoAuthApi,
            profile,
          }),
        );
      } finally {
        if (isMounted) {
          setIsRestoringSession(false);
        }
      }
    };

    restoreSession();
    return () => {
      isMounted = false;
    };
  }, [handleSignInSuccess, logtoAuthApi]);

  if (isRestoringSession) {
    return <FullScreenContainer />;
  }

  const content = isContactUsRoute ? (
    <ContactUsPage />
  ) : (
    <LandingPage onLoginClick={openLogin} />
  );

  return isNewLandingPage ? (
    <RootWithLandingPageView onOpenLogin={openLogin}>
      {content}
    </RootWithLandingPageView>
  ) : (
    <Root>{content}</Root>
  );
}
